// deps
import React, { ReactElement, useState, useMemo, useContext, useEffect } from "react";

// external-deps
import { DineoutCartContext, GlobalHeaderState, SwgyHeaderKeysList } from "@swiggy-private/checkout-core";

// contexts
import {
    DineoutSharedCartContext,
    DineoutSharedCartContextValue,
} from "@dineout-cart-client/contexts/DineCashPoolingContext";

// helpers
import { clearDineCashPoolingIntent, readDineCashPoolingIntent } from "@dineout-cart-client/helpers/DineCashPooling";
import { isEmpty } from "@swiggy-private/js-utils";

interface Props {
    children: React.ReactNode;
}

export function DineoutSharedCartProvider({ children }: Props): ReactElement {
    const { cartState } = useContext(DineoutCartContext);
    const rewardsPoolWidget = cartState?.reviewedCart?.data?.rewardsPoolWidget;
    const cartId = cartState?.reviewedCart?.data?.cartId || "";
    const [userIntent, setUserIntent] = useState(false);
    const [lastKnownUpdatedAt, setLastKnownUpdatedAt] = useState<string | null>(null);
    const [returnedFromPoolPage, setReturnedFromPoolPage] = useState(false);
    const [contributionsArrivedOnPoolPage, setContributionsArrivedOnPoolPage] = useState(false);
    const maxPoolAmountAllowed = Number(rewardsPoolWidget?.maxPoolAmountAllowed) || 0;
    const maxNoOfContributorsAllowed = Number(rewardsPoolWidget?.maxNoOfContributor) || 0;
    const maxPerPersonCap = Number(rewardsPoolWidget?.maxPerHead) || 0;
    const poolType = rewardsPoolWidget?.flowType || "";

    useEffect(() => {
        const userId = GlobalHeaderState.getHeader(SwgyHeaderKeysList.userId) || "";
        const stored = readDineCashPoolingIntent();

        // if rewardsPoolWidget is not present, clear the intent
        if (isEmpty(rewardsPoolWidget)) {
            if (!isEmpty(stored) && userId && cartId && stored.userId === userId && stored.cartId === cartId) {
                clearDineCashPoolingIntent();
                setUserIntent(false);
            }
            return;
        }

        // if cartId or userId is not present, return
        if (!cartId || !userId) {
            return;
        }

        // if stored intent matches the cartId and userId, set the userIntent to true
        if (!isEmpty(stored) && stored.cartId === cartId && stored.userId === userId) {
            setUserIntent(true);
            return;
        }

        // if stored intent does not match the cartId and userId, clear the intent and set the userIntent to false
        if (!isEmpty(stored) && stored.userId === userId && stored.cartId !== cartId) {
            clearDineCashPoolingIntent();
            setUserIntent(false);
        }
    }, [rewardsPoolWidget, cartId, setUserIntent]);

    const value = useMemo<DineoutSharedCartContextValue>(
        () => ({
            userIntent,
            setUserIntent,
            maxPoolAmountAllowed,
            maxNoOfContributorsAllowed,
            maxPerPersonCap,
            poolType,
            cartId,
            lastKnownUpdatedAt,
            setLastKnownUpdatedAt,
            returnedFromPoolPage,
            setReturnedFromPoolPage,
            contributionsArrivedOnPoolPage,
            setContributionsArrivedOnPoolPage,
        }),
        [userIntent, maxPoolAmountAllowed, maxNoOfContributorsAllowed, maxPerPersonCap, poolType, cartId, lastKnownUpdatedAt, returnedFromPoolPage, contributionsArrivedOnPoolPage],
    );

    return <DineoutSharedCartContext.Provider value={value}>{children}</DineoutSharedCartContext.Provider>;
}
